import React, { useEffect, useState, useMemo } from 'react';
import { getNotSendedEmails, sendQueueEmail, removeQueueEmail, exportQueueEmails, getCompanies } from '../api/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from "./ui/sheet"
import { toast } from "sonner"
import { Download, Search } from 'lucide-react';
import EmailTable from './EmailTable';

import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "../lib/utils"
import { Calendar } from "./ui/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "./ui/popover"

export default function NotSendedEmails() {
    const [emails, setEmails] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [companyFilter, setCompanyFilter] = useState('');
    const [statusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [sortBy] = useState('createdAt');
    const [sortOrder] = useState('desc');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [companies, setCompanies] = useState([]);

    useEffect(() => {
        async function fetchCompanies() {
            try {
                const res = await getCompanies();
                setCompanies(res.data.companies);
            } catch (error) {
                console.error("Failed to fetch companies", error);
            }
        }
        fetchCompanies();
    }, []);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            const filters = {
                search,
                company: companyFilter === 'all' ? '' : companyFilter,
                status: statusFilter === 'all' ? '' : statusFilter,
                date: dateFilter,
                sort: sortBy,
                order: sortOrder
            };
            const res = await getNotSendedEmails(page + 1, rowsPerPage, filters);
            setEmails(res.data.emails);
            setTotal(res.data.total);
            setLoading(false);
        }
        fetchData();
    }, [page, rowsPerPage, search, companyFilter, statusFilter, dateFilter, sortBy, sortOrder]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredEmails = useMemo(() => emails, [emails]);

    // Actions
    const handleRetry = async (id) => {
        try {
            await sendQueueEmail(id);
            toast.success('Resent successfully!');
            setEmails(emails => emails.map(e => e._id === id ? { ...e, isSend: true } : e));
        } catch (err) {
            toast.error('Error resending!');
        }
    };
    const handleRemove = async (id) => {
        try {
            await removeQueueEmail(id);
            toast.success('Removed from queue!');
            setEmails(emails => emails.filter(e => e._id !== id));
        } catch (err) {
            toast.error('Error deleting!');
        }
    };
    const handleExport = async () => {
        try {
            const res = await exportQueueEmails();
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'not_sent_export.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            toast.success('CSV exported!');
        } catch (err) {
            toast.error('Export error!');
        }
    };
    const handleDeleteClick = (row) => {
        setDeleteTarget(row);
        setDeleteDialogOpen(true);
    };
    const handleDeleteConfirm = () => {
        if (deleteTarget) handleRemove(deleteTarget._id);
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
    };
    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
    };
    const exportAsCSV = (row) => {
        const csv = `To,Subject,Body\n"${row.to}","${row.subject}","${row.body?.replace(/"/g, '""')}"`;
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mail_${row._id}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };
    const exportAsEML = (row) => {
        const eml = `To: ${row.to}\nSubject: ${row.subject}\n\n${row.body || ''}\n\n${row.signature || ''}`;
        const blob = new Blob([eml], { type: 'message/rfc822' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mail_${row._id}.eml`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6 p-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight">Queued Emails</h2>
                    <p className="text-muted-foreground mt-1">
                        Emails that have been saved but not yet processed.
                    </p>
                </div>
                <Button variant="outline" onClick={handleExport} className="font-semibold">
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                </Button>
            </div>

            {/* Scale Filters */}
            <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-card p-4 rounded-lg border shadow-sm">
                <div className="flex flex-col space-y-1.5 w-full sm:w-[200px]">
                    <Label htmlFor="company-filter">Company</Label>
                    <Select value={companyFilter} onValueChange={setCompanyFilter}>
                        <SelectTrigger id="company-filter">
                            <SelectValue placeholder="All Companies" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Companies</SelectItem>
                            {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex flex-col space-y-1.5 w-full sm:w-[240px]">
                    <Label htmlFor="date-filter">Date</Label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant={"outline"}
                                className={cn(
                                    "w-full justify-start text-left font-normal",
                                    !dateFilter && "text-muted-foreground"
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {dateFilter ? format(new Date(dateFilter), "PPP") : <span>Pick a date</span>}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                            <Calendar
                                mode="single"
                                selected={dateFilter ? new Date(dateFilter) : undefined}
                                onSelect={(date) => setDateFilter(date ? format(date, 'yyyy-MM-dd') : '')}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="flex flex-col space-y-1.5 w-full flex-1">
                    <Label htmlFor="search">Search</Label>
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            id="search"
                            type="search"
                            placeholder="Search emails, names, company..."
                            className="pl-9"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <EmailTable
                emails={filteredEmails}
                loading={loading}
                total={total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onRetry={handleRetry}
                onDelete={handleDeleteClick}
                onExportCSV={exportAsCSV}
                onExportEML={exportAsEML}
                pageType="queued"
            />

            <Sheet open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <SheetContent side="right">
                    <SheetHeader>
                        <SheetTitle>Delete Email</SheetTitle>
                        <SheetDescription>
                            Are you sure you want to delete this email from the queue? This action cannot be undone.
                        </SheetDescription>
                    </SheetHeader>
                    <SheetFooter>
                        <Button variant="outline" onClick={handleDeleteCancel}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDeleteConfirm}>Delete</Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>
        </div>
    );
} 